/** @jsx jsx */
import React, { useRef, useMemo } from 'react';
import { jsx } from '@emotion/core';
import { useDebounce } from 'use-debounce';

import { Box } from '@material-ui/core';

import styles from './styles';

import PageContainer from 'components/PageContainer';
import Title from 'components/Title';
import Button from 'components/Button';
import ActionButtonsContainer from 'components/ActionButtonsContainer';
import HorizontalRule from 'components/HorizontalRule';
import { TextFieldPopover } from 'components/TextField';

import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';

import { ExpandedPanel } from 'state/unifiedWidgets';
import {
  InstallmentsInitialOffer,
  SplitPayInitialOffer,
} from 'state/initialFinanceOffers';
import isPresent from 'utils/isPresent';

import AlertBar from 'components/AlertBar';
import { FormikProps, Form, Field, FieldProps } from 'formik';

import { FormValues } from './CheckoutOfferIntro';

import MiniIntendedPurchaseAmount from 'components/unified/MiniIntendedPurchaseAmount';
import Installments from '../OfferIntro/Installments';
import SplitPay from '../OfferIntro/SplitPay';
import CreditCard from '../OfferIntro/CreditCard';
import { conformToMask, decimalMoneyMask } from 'utils/masks';
import { useSelector } from 'react-redux';
import { selectTenant, isOfferConfigured } from 'state/tenant';
import BreadIcon from '../Offer/BreadIcon';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import { OfferWidgetType } from 'hooks/useAnalyticVariables';
import { selectCreditApplication } from 'state/creditApplication';
import FocusableHelpOutlineIcon from 'components/TextField/FocusableHelpOutlineIcon';
interface PropTypes {
  formikProps: FormikProps<FormValues>;
  intendedPurchaseAmount: string | null;
  installmentOffers: InstallmentsInitialOffer[];
  splitPayOffers: SplitPayInitialOffer[];
  onPanelChange: (
    event: React.ChangeEvent<{}>,
    panelName: string,
    expanded: boolean,
  ) => void;
  expandedPanel: ExpandedPanel | undefined;
  defaultCartAmount: number;
}

const CheckoutOfferIntroView: React.FC<PropTypes> = props => {
  const {
    intendedPurchaseAmount,
    defaultCartAmount,
    installmentOffers,
    splitPayOffers,
    onPanelChange,
    expandedPanel,
    formikProps: { errors, isSubmitting, isValid },
  } = props;

  const softInquiryRef = useRef<HTMLElement | null>(null);
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const { bread } = useSelector(selectTenant);

  const splitPayPresent =
    isOfferConfigured('splitpay', bread) && isPresent(splitPayOffers);
  const installmentsPresent =
    isOfferConfigured('installments', bread) && isPresent(installmentOffers);

  const principal = useMemo(
    () =>
      intendedPurchaseAmount
        ? parseFloat(intendedPurchaseAmount)
        : defaultCartAmount,
    [defaultCartAmount, intendedPurchaseAmount],
  );

  const [principalDebounced] = useDebounce(principal, 200);
  const [intendedPurchaseAmountError] = useDebounce(
    errors.intendedPurchaseAmount,
    200,
  );

  const installmentId: OfferWidgetType = 'installments';
  const installments = installmentsPresent ? (
    <div
      key="installments"
      id={installmentId}
      css={styles.paymentOptionContainer}
    >
      <Installments
        intendedPurchaseAmount={principalDebounced}
        panelName="installments"
        expanded={expandedPanel === 'installments'}
        onChange={onPanelChange}
        installmentOffers={installmentOffers}
      />
    </div>
  ) : null;

  const splitPayId: OfferWidgetType = 'splitpay';
  const splitPay = splitPayPresent ? (
    <div
      key={splitPayOffers[0].id}
      id={splitPayId}
      css={styles.paymentOptionContainer}
    >
      <SplitPay
        key={splitPayOffers[0].id}
        intendedPurchaseAmount={principalDebounced}
        panelName={'splitpay'}
        expanded={expandedPanel === 'splitpay'}
        onChange={onPanelChange}
        splitPayOffer={splitPayOffers[0]}
      />
    </div>
  ) : null;

  const divider =
    (installmentsPresent || splitPayPresent) &&
    isOfferConfigured('prequalification', bread) ? (
      <div key="divider" css={styles.paymentOptionContainer}>
        <HorizontalRule orString={t('unified.orHorizontalRule')} />
      </div>
    ) : null;

  const prequalificationId: OfferWidgetType = 'prequalOffer';
  const prequalification = (isOfferConfigured(
    'prequalification',
    bread,
  ) as boolean) && (
    <div
      key="credit"
      id={prequalificationId}
      css={styles.paymentOptionContainer}
    >
      <CreditCard
        expanded={expandedPanel === 'prequalification'}
        panelName={'prequalification'}
        handlePanelExpandChange={onPanelChange}
        intendedPurchaseAmount={intendedPurchaseAmount}
      />
    </div>
  );

  const eligibilityCheckContainer = isOfferConfigured(
    'prequalification',
    bread,
  ) && (
    <div css={styles.eligibilityCheckContainer}>
      {tMd('unified.offerIntro.eligibility')}
      <span css={styles.enquiryIcon}>
        <FocusableHelpOutlineIcon innerRef={softInquiryRef} />
      </span>
      <TextFieldPopover id="softInquiry" textFieldRef={softInquiryRef}>
        {tMdMultiline('unified.offerIntro.softInquiry')}
      </TextFieldPopover>
    </div>
  );
  const firstOffers = useRef<ExpandedPanel | undefined>(expandedPanel);

  const widgets = useMemo(() => {
    switch (firstOffers.current) {
      case 'installments':
        return [installments, splitPay, divider, prequalification];
      case 'splitpay':
        return [splitPay, installments, divider, prequalification];
      case 'prequalification':
        return [prequalification, divider, installments, splitPay];
      default:
        return [installments, splitPay, divider, prequalification];
    }
  }, [divider, installments, prequalification, splitPay]);

  const creditApplication = useSelector(selectCreditApplication);

  const hasEditableIntendedPurchaseAmount =
    creditApplication.checkoutAmount ||
    creditApplication.cartAmount ||
    creditApplication.productAmount ||
    (creditApplication.order && creditApplication.order.totalPriceValue);

  return (
    <Form noValidate>
      <PageContainer maxWidth="sm">
        <Box mb={2}>
          <Title css={styles.pageTitleText}>
            {tMd('unified.offerIntro.title')}
          </Title>
        </Box>
        <Box mb={2} css={styles.breadSubtitle}>
          {tMd('unified.offerIntro.breadSubtitle.text')}
          <BreadIcon
            id={'breadSubtitleLogo'}
            logo={t('unified.offerIntro.breadSubtitle.breadLogo')}
          />
        </Box>
        <Box mb={2} css={styles.subTitleText}>
          {tMd('unified.offerIntro.subtitle')}
        </Box>
        {!hasEditableIntendedPurchaseAmount ? (
          <Box mb={2} css={styles.intendedPurchaseAmount}>
            <Field name="intendedPurchaseAmount">
              {(fieldProps: FieldProps) => {
                return <MiniIntendedPurchaseAmount {...fieldProps.field} />;
              }}
            </Field>
          </Box>
        ) : (
          <Box
            mb={2}
            css={styles.fixedIntendedPurchaseAmount}
            aria-label="fixedIntendedPurchaseAmount"
          >
            {conformToMask(principalDebounced.toFixed(2), decimalMoneyMask)}
          </Box>
        )}
        <AlertBar severity="error" open={Boolean(intendedPurchaseAmountError)}>
          {intendedPurchaseAmountError}
        </AlertBar>

        {widgets}

        <StickyFooter>
          <div css={styles.footer}>
            {eligibilityCheckContainer}
            <ActionButtonsContainer>
              <Button
                mobileFullWidth={true}
                variant="contained"
                type="submit"
                disabled={!isValid || isSubmitting || !expandedPanel}
                loading={isSubmitting}
              >
                {tMd(
                  'unified.offerIntro.paymentOption.creditCard.getStartedButton',
                )}
              </Button>
            </ActionButtonsContainer>
          </div>
        </StickyFooter>
        <div css={styles.finePrint}>
          {tMdMultiline('unified.offerIntro.finePrint')}
        </div>
      </PageContainer>
    </Form>
  );
};

export default CheckoutOfferIntroView;
